import type { ThemePreference } from '@shared/types'
import { Badge, Card, CardBody, CardDescription, CardHeader, CardTitle, SegmentedControl } from '@/components/ui'
import { cn } from '@/lib/cn'
import { useThemeStore } from '@/stores/useThemeStore'

const THEME_OPTIONS = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' }
]

function isThemePreference(value: string): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system'
}

/**
 * The previews reference palette stops directly rather than the semantic
 * tokens: their whole job is to depict a theme that is *not* currently applied,
 * so they must not inherit the surrounding one.
 */
const SCHEMES = {
  light: {
    frame: 'border-ink-200 bg-ink-100',
    rail: 'border-ink-200 bg-ink-50',
    dot: 'bg-ink-300',
    heading: 'bg-ink-400',
    line: 'bg-ink-200',
    accent: 'bg-brand-600'
  },
  dark: {
    frame: 'border-ink-800 bg-ink-950',
    rail: 'border-ink-800 bg-ink-900',
    dot: 'bg-ink-700',
    heading: 'bg-ink-600',
    line: 'bg-ink-800',
    accent: 'bg-brand-500'
  }
} as const

type Scheme = keyof typeof SCHEMES

const RAIL_DOTS = ['a', 'b', 'c']

function ThemePreview({ scheme }: { scheme: Scheme }): React.ReactElement {
  const tone = SCHEMES[scheme]

  return (
    <div
      aria-hidden="true"
      className={cn('flex h-[5.5rem] overflow-hidden rounded-control border', tone.frame)}
    >
      <div className={cn('flex w-9 shrink-0 flex-col gap-1.5 border-r p-2', tone.rail)}>
        {RAIL_DOTS.map((key) => (
          <span key={key} className={cn('h-1.5 rounded-full', tone.dot)} />
        ))}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2 p-2.5">
        <span className={cn('h-2 w-1/2 rounded-full', tone.heading)} />
        <span className={cn('h-1.5 w-full rounded-full', tone.line)} />
        <span className={cn('h-1.5 w-4/5 rounded-full', tone.line)} />
        <span className={cn('mt-auto h-3.5 w-12 rounded-[0.35rem]', tone.accent)} />
      </div>
    </div>
  )
}

export default function AppearanceCard(): React.ReactElement {
  const theme = useThemeStore((state) => state.theme)
  const resolved = useThemeStore((state) => state.resolved)
  const setTheme = useThemeStore((state) => state.setTheme)

  const previews: Scheme[] = theme === 'system' ? ['light', 'dark'] : [theme]

  return (
    <Card>
      <CardHeader>
        <CardTitle>Appearance</CardTitle>
        <CardDescription>
          Applies to this device the moment you pick it, and is saved to your account so the next
          browser you sign in from starts out the same way.
        </CardDescription>
      </CardHeader>

      <CardBody className="space-y-5">
        <SegmentedControl
          label="Colour theme"
          value={theme}
          options={THEME_OPTIONS}
          onChange={(value) => {
            if (isThemePreference(value)) setTheme(value)
          }}
        />

        <div>
          <div className={cn('grid gap-3', previews.length > 1 && 'sm:grid-cols-2')}>
            {previews.map((scheme) => (
              <div key={scheme}>
                <ThemePreview scheme={scheme} />
                <p className="mt-2 flex items-center gap-2 text-xs font-medium text-fg-muted">
                  <span className="capitalize">{scheme}</span>
                  {theme === 'system' && scheme === resolved ? (
                    <Badge variant="accent" size="sm">
                      In use now
                    </Badge>
                  ) : null}
                </p>
              </div>
            ))}
          </div>

          <p className="mt-3 text-xs leading-relaxed text-fg-subtle">
            {theme === 'system'
              ? `System follows your device, which is asking for ${resolved} right now. Change it there and Argus changes with it.`
              : `Argus stays ${theme} here, whatever your device is set to.`}
          </p>
        </div>
      </CardBody>
    </Card>
  )
}

export { AppearanceCard }
